import React, { useCallback, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { FilePlus2, FileUp } from 'lucide-react';
import { useChatStore } from '@auw/stores';
import { useWorkspaceNavigate } from '@auw/workspace-features';
import { useTranslation } from 'react-i18next';

type SupportedMode = 'document';

interface Props {
  mode: SupportedMode;
}

export const WorkspaceModeStartPage: React.FC<Props> = ({ mode }) => {
  const { t } = useTranslation('common');
  const setConversationStartPagePending = useChatStore((s) => s.setConversationStartPagePending);
  const { createWorkspaceItem } = useWorkspaceNavigate();
  const activeId = useChatStore((s) => s.activeId);
  const activeConversation = useChatStore((s) =>
    s.activeId ? s.conversations[s.activeId] ?? null : null,
  );
  const isPending = !!activeConversation
    && activeConversation.mode === mode
    && activeConversation.startPagePending === true;
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fileAccept = useMemo(() => {
    if (mode === 'document') return '.txt,.md,.doc,.docx,.hwp,.hwpx,.pdf';
    return '*/*';
  }, [mode]);

  const activatePage = useCallback(() => {
    if (isPending && activeId) {
      setConversationStartPagePending(activeId, false);
      return activeId;
    }
    return createWorkspaceItem({ mode, scope: 'personal', kind: 'page' });
  }, [activeId, createWorkspaceItem, isPending, mode, setConversationStartPagePending]);

  const createPage = () => {
    activatePage();
  };

  const dispatchFiles = (files: File[]) => {
    if (files.length === 0) return;
    window.dispatchEvent(new CustomEvent('chat:addFiles', { detail: { files } }));
  };

  const handleImportedFiles = (files: File[]) => {
    if (files.length === 0) return;
    activatePage();
    dispatchFiles(files);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleImportedFiles(Array.from(e.target.files ?? []));
    e.currentTarget.value = '';
  };

  return (
    <Container>
      <Title>{t('workspace.letsStartToday')}</Title>

      {mode === 'document' && (
        <Cards>
          <ModeCard type="button" onClick={() => fileInputRef.current?.click()}>
            <CardIcon>
              <FileUp size={18} />
            </CardIcon>
            <CardLabel>{t('workspace.importDocument')}</CardLabel>
          </ModeCard>
          <ModeCard type="button" onClick={createPage}>
            <CardIcon>
              <FilePlus2 size={18} />
            </CardIcon>
            <CardLabel>{t('workspace.startBlankPage')}</CardLabel>
          </ModeCard>
        </Cards>
      )}

      <HiddenFileInput
        ref={fileInputRef}
        type="file"
        accept={fileAccept}
        onChange={handleFileChange}
        multiple
      />
    </Container>
  );
};

const Container = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 24px;
  padding: 40px 20px;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 1.75rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary};
  letter-spacing: -0.025em;
`;

const Cards = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(180px, 1fr));
  gap: 12px;
  width: min(560px, 100%);

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: 10px;
  }
`;

const ModeCard = styled.button<{ $selected?: boolean }>`
  border: 1px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)'};
  border-radius: 14px;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.8)'};
  backdrop-filter: blur(12px) saturate(160%);
  -webkit-backdrop-filter: blur(12px) saturate(160%);
  color: ${({ theme }) => theme.colors.textPrimary};
  padding: 18px 20px;
  min-height: 78px;
  display: flex;
  align-items: center;
  gap: 14px;
  text-align: left;
  cursor: pointer;
  box-shadow: ${({ theme }) =>
    theme.mode === 'dark'
      ? '0 2px 8px rgba(0,0,0,0.25)'
      : '0 2px 8px rgba(0,0,0,0.04)'};
  transition: all 0.25s cubic-bezier(0.22, 1, 0.36, 1);

  &:hover {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.07)' : '#ffffff'};
    border-color: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)'};
    box-shadow: ${({ theme }) =>
      theme.mode === 'dark'
        ? '0 4px 16px rgba(0,0,0,0.35)'
        : '0 4px 16px rgba(0,0,0,0.08)'};
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0) scale(0.99);
  }
`;

const CardIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 22%;
  background: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'linear-gradient(145deg, #434345, #38383a)'
      : 'linear-gradient(145deg, #eaeaed, #e1e1e5)'};
  box-shadow: ${({ theme }) =>
    theme.mode === 'dark'
      ? '0 1px 4px rgba(0,0,0,0.35)'
      : '0 1px 4px rgba(0,0,0,0.08)'};
  color: ${({ theme }) => (theme.mode === 'dark' ? '#c8c8cc' : '#4a4a4e')};
  flex-shrink: 0;
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);

  ${ModeCard}:hover & {
    transform: scale(1.08);
  }
`;

const CardLabel = styled.span`
  font-size: 0.9rem;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const HiddenFileInput = styled.input`
  display: none;
`;
